/* eslint-disable @typescript-eslint/no-empty-function */
import * as React from 'react'
import tw from 'tailwind-styled-components'
import type { Response } from 'types/response'
import type { SessionObject } from 'types/session-object'
import Radio from 'ui/atoms/Radio'
import cn from 'utils/tailwind/cn'

import { TriggerUserActions } from '@/components/TriggerUserActions'

import { FakeInput } from '../form/FakeInput'
import BaseField, { type Props as FieldProps } from './BaseField'
import TextField from './TextField'

type Choice = {
  id: string
  label: string
  value: string
  requestDetails?: boolean
}

type Props = {
  questionId: string | number
  choices: Array<Choice>
  getDetails?: (value: string) => string
  isChecked?: (value: string) => boolean
  numberOfColumns?: number
  onChange: (value: string, details?: string) => void
  onChangeDetails: (
    value: string,
    details: string | React.ChangeEvent<HTMLInputElement>
  ) => void
  readonly?: boolean
  willShowNotAllowedCursor?: boolean
  response: Response
  sessions?: SessionObject
} & FieldProps

const RadioGrid = tw.div`
  grid
  grid-cols-1
  gap-x-4
  gap-y-[3px]

  ${(props: { $numberOfColumns: number }) =>
    `sm:grid-cols-${props.$numberOfColumns}`}
`

function RadioField({
  choices,
  getDetails,
  isChecked = () => false,
  hasError,
  numberOfColumns = 1,
  onChange = () => {},
  onChangeDetails = () => {},
  readonly,
  willShowNotAllowedCursor,
  questionId,
  response,
  sessions,
  ...props
}: Props): React.ReactNode {
  const handleChange = (value: string, details?: string) => {
    if (typeof onChange === 'function') {
      onChange(value, details)
    }
  }

  const handleChangeDetails = (
    value: string,
    details: string | React.ChangeEvent<HTMLInputElement>
  ) => {
    if (typeof onChangeDetails === 'function') {
      onChangeDetails(value, details)
    }
  }

  return (
    <BaseField {...props} semibold hasError={hasError}>
      <RadioGrid $numberOfColumns={numberOfColumns}>
        {choices.map((choice) => {
          let isChoiceSelectedAndRequiresDetails = false
          if (isChecked(choice.value) && choice.requestDetails) {
            isChoiceSelectedAndRequiresDetails = true
          }

          const requiresDetailsStyle = {
            'flex-col': isChoiceSelectedAndRequiresDetails,
          }
          return (
            <div
              className={cn('flex space-x-2', requiresDetailsStyle)}
              key={choice.value}
            >
              <TriggerUserActions>
                <Radio
                  checked={isChecked(choice.value)}
                  data-form-id={response?.form?.id}
                  data-packet-id={
                    sessions?.fillResponseCustomer?.packetResponse?.packet?.id
                  }
                  data-response-id={response?.id}
                  disabled={!!readonly}
                  inputId={`q-${questionId || ''}-c-${choice.id}`}
                  label={choice.label}
                  onChange={() => handleChange(choice.value)}
                  willShowNotAllowedCursor={!!willShowNotAllowedCursor}
                />
              </TriggerUserActions>
              {isChecked(choice.value) && choice.requestDetails && (
                <div className="mb-2 flex-1 !pl-5">
                  {readonly && getDetails?.(choice.value) ? (
                    <FakeInput>{getDetails?.(choice.value)}</FakeInput>
                  ) : null}

                  {!readonly ? (
                    <TextField
                      className="min-h-[38px] py-1"
                      onBlur={(event) =>
                        handleChange(choice.value, event?.target?.value)
                      }
                      onChange={(value) =>
                        handleChangeDetails(choice.value, value)
                      }
                      placeholder="Details"
                      value={getDetails ? getDetails(choice.value) : ''}
                    />
                  ) : null}
                </div>
              )}
            </div>
          )
        })}
      </RadioGrid>
    </BaseField>
  )
}

export default RadioField
